import { useEffect, useMemo, useState } from "react";

import { AvgActiveChart } from "../../components/charts3";
import AvgActiveEpgChart from "../../components/charts3/AvgActiveEpgChart";
import { ChartControls } from "../../components/Channel";

import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useDateInputs } from "../../hooks/useDateInputs";

import { useAvgActiveSeries } from "../../hooks/useAvgActiveSeries";
import { useEpgData } from "../../hooks/useEpgData";

import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
import { programEndClock } from "../../utils/epgTime";


const PROGRAM_COLORS = [
  '#E6194B','#3CB44B','#FFE119','#4363D8','#F58231',
  '#911EB4','#46F0F0','#F032E6','#BCF60C','#FABEBE',
  '#008080','#E6BEFF','#9A6324','#FFFAC8','#800000'
];

function getProgramColor(name,map){
  if(!map[name]){
    const idx = Object.keys(map).length % PROGRAM_COLORS.length;
    map[name] = PROGRAM_COLORS[idx];
  }
  return map[name];
}

export default function ChannelAvgActive({
  channelId,
  channelInfo,
  rawVideo,
  refreshing,
  refreshKey,
  refreshEnabled,
  setRefreshEnabled,
  refreshIntervalMin,
  setRefreshIntervalMin,
  refreshCountdown,
  manualRefresh
}){

  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");

  // ✅ DEFAULT FIX
  const defaultMode = "hour";
  const isDark = !!storedUser.darkmode;

  // ✅ MODE STATE
  const [mode,setMode] = useState(defaultMode);
  const [selectedProgram,setSelectedProgram] = useState(null);

  const [startTime,setStartTime] = useState("00:00");
  const [endTime,setEndTime] = useState("23:59");

  const sortedKeys = useDaysSorted(rawVideo);
  const dates = useDateInputs(sortedKeys,mode);

  const series = useAvgActiveSeries({
    rawVideo,
    sortedVideoKeys: sortedKeys,
    startDateInput: dates.startDateInput,
    endDateInput: dates.endDateInput,
    startTime,
    endTime,
    mode,
  });

  const { logPageChange,logTimeframeChange } = useUserJourneyLogger({
    channelId,
    channelName: channelInfo?.name || channelInfo?.n || "unknown",
  });

  useEffect(()=>{
    logPageChange({
      chartType:"avgactivetv",
    });
  },[logPageChange]);

  // ✅ MODE LOCK (SADECE HOUR)
  const handleModeChange=(newMode)=>{

    if(newMode !== "hour") return;

    setMode("hour");

    logTimeframeChange({
      chartType:"avgactivetv",
      mode:"hour",
    });
  };

  useEffect(()=>{
    if(mode==="minute"){
      setStartTime("00:00");
      setEndTime("23:59");
    }
  },[mode]);

  const { epgBlocks, loading: epgLoading } = useEpgData(channelId, dates.startDateInput);
  const epgForSelectedDate = useMemo(()=>{
    if((mode!=="epg" && mode!=="minute") || !dates.startDateInput) return [];

    return epgBlocks
      .filter(b=>b.keyDate===dates.startDateInput)
      .sort((a,b)=>a.startMinute-b.startMinute);

  },[mode,dates.startDateInput,epgBlocks]);

  const minuteSeries = useMemo(()=>{

    if(mode!=="epg" || !series?.length) return [];

    return series.map(p=>({
      minute:p.index ?? 0,
      value:Number(p.value)||0
    }));

  },[mode,series]);

  const programWindows = useMemo(()=>{

    if(!epgForSelectedDate.length) return [];

    const colorMap = {};

    let programs = epgForSelectedDate;

    if(selectedProgram){
      programs = epgForSelectedDate.filter(p=>p.id===selectedProgram.id);
    }

    return programs.map(p=>({
      name:p.programName,
      startMinute:p.startMinute,
      endMinute:p.endMinute,
      color:getProgramColor(p.programName,colorMap)
    }));

  },[epgForSelectedDate,selectedProgram]);

  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={mode}
        onModeChange={handleModeChange}
        startDateInput={dates.startDateInput}
        endDateInput={dates.endDateInput}
        onStartDateChange={dates.setStartDateInput}
        onEndDateChange={dates.setEndDateInput}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        allowedModes={[]} // ✅ SADECE HOUR
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {/* ❌ EPG/MINUTE UI DISABLED AMA KOD DURUYOR */}
      {(mode === "epg" || mode === "minute") && false && !epgLoading && (

        <div className={`epg-drawer ${isDark?"dark":"light"}`}>

          <label style={{fontWeight:600,fontSize:14}}>
            Select Program:
          </label>

          <select
            value={selectedProgram?.id || ""}
            onChange={(e)=>{

              const prog = epgForSelectedDate.find(p=>p.id===e.target.value);

              setSelectedProgram(prog||null);

              if(prog){

                const startH = String(Math.floor(prog.startMinute/60)).padStart(2,"0");
                const startM = String(prog.startMinute%60).padStart(2,"0");


                setStartTime(`${startH}:${startM}`);
                setEndTime(programEndClock(prog));

              }else{
                setStartTime("00:00");
                setEndTime("23:59");
              }

            }}
            style={{
              flex:1,
              maxWidth:600,
              padding:'8px 12px',
              fontSize:14,
              borderRadius:8,
              cursor:'pointer'
            }}
          >

            <option value="">
              00:00 - 23:59 | Full Day
            </option>

            {epgForSelectedDate.map(prog=>{

              const startH = String(Math.floor(prog.startMinute/60)).padStart(2,"0");
              const startM = String(prog.startMinute%60).padStart(2,"0");

              const endH = String(Math.floor(prog.endMinute/60)).padStart(2,"0");
              const endM = String(prog.endMinute%60).padStart(2,"0");

              return(
                <option key={prog.id} value={prog.id}>
                  {startH}:{startM} - {endH}:{endM} | {prog.programName}
                </option>
              );

            })}

          </select>

        </div>

      )}

      {/* ❌ EPG CHART DISABLED */}
      {mode==="epg" && false && (
        <AvgActiveEpgChart
          minuteSeries={minuteSeries}
          programSeries={programWindows}
        />
      )}

      {/* ✅ SADECE BU ÇALIŞIR */}
      {mode!=="epg" && (
        <AvgActiveChart
          key={refreshKey}
          series={series}
          mode={mode}
        />
      )}
    </>
  );
}